'use client';

import React from 'react';
import { useFilters } from '@/context/FiltersContext';
import type { Filters } from '@/types/filters';

interface Props {
  filterKey: keyof Pick<Filters, 'mrrRange' | 'growthRange' | 'customersRange'>;
  label: string;
  minDefault?: number;
  maxDefault?: number;
  placeholder?: [string, string];
  allowNegativeMin?: boolean;
}

export const RangeFilter: React.FC<Props> = ({
  filterKey,
  label,
  minDefault = 0,
  maxDefault = Infinity,
  placeholder = ['Min', 'Max'],
  allowNegativeMin = false,
}) => {
  const { filters, updateFilter } = useFilters();
  const [min, max] = filters[filterKey] as [number, number];

  const handleMin = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.value === '') {
      updateFilter(filterKey, [minDefault, max] as Filters[typeof filterKey]);
      return;
    }
    const val = allowNegativeMin ? (Number(e.target.value) || minDefault) : Math.max(0, Number(e.target.value) || 0);
    updateFilter(filterKey, [val, max] as Filters[typeof filterKey]);
  };

  const handleMax = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.value === '') {
      updateFilter(filterKey, [min, maxDefault] as Filters[typeof filterKey]);
      return;
    }
    updateFilter(filterKey, [min, Math.max(0, Number(e.target.value) || 0)] as Filters[typeof filterKey]);
  };

  const minDisplay = min === minDefault ? '' : min;
  const maxDisplay = max === maxDefault ? '' : max;

  const inputClass = 'w-full bg-bg-raised border border-border text-text-secondary text-[11px] px-[8px] py-[5px] rounded-sm placeholder:text-text-muted focus:outline-none focus:border-border-mid [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none';

  return (
    <div>
      <div className="text-[9px] font-semibold tracking-[0.12em] uppercase text-text-muted pb-[6px]">{label}</div>
      <div className="flex gap-[6px] items-center">
        <input
          type="number"
          value={minDisplay}
          onChange={handleMin}
          placeholder={placeholder[0]}
          aria-label={`Minimum ${label}`}
          className={inputClass}
        />
        <span className="text-text-muted text-[10px] flex-shrink-0">–</span>
        <input
          type="number"
          min={0}
          value={maxDisplay}
          onChange={handleMax}
          placeholder={placeholder[1]}
          aria-label={`Maximum ${label}`}
          className={inputClass}
        />
      </div>
    </div>
  );
};
